import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretUp, DotsSixVertical, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TRADER_COLUMNS, type TraderColumn } from '@/lib/traderColumns';

/*
 * The Top Traders column picker, shared (2026-10-05): the Dashboard's
 * leaderboard and an asset's traders (Guillaume: "like the Top Traders
 * Leaderboard on the dashboard, with like adjustable columns"). Choose the
 * columns, drag them into order; the array IS the display order.
 */
export function TraderColumnPicker({ visible, onChange, defaults }: {
  visible: string[];
  onChange: (next: string[]) => void;
  defaults: string[];
}) {
  const { t } = useTranslation('dashboard');
  const shown = visible.map((k) => TRADER_COLUMNS.find((c) => c.key === k)).filter(Boolean) as TraderColumn[];
  // The order lives here while a drag is under way and is saved once, when it
  // ends: a write per row crossed would save a half-finished order.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[] | null>(null);
  const order = draft ?? visible;
  const moveTo = (key: string, to: number, from: string[] = order) => {
    const at = from.indexOf(key);
    if (at < 0 || to < 0 || to >= from.length || at === to) return from;
    const next = [...from];
    next.splice(to, 0, next.splice(at, 1)[0]);
    return next;
  };
  const commit = () => {
    if (draft) onChange(draft);
    setDraft(null);
    setDragKey(null);
  };
  const toggle = (key: string) => onChange(visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key]);
  const rows = order.map((k) => TRADER_COLUMNS.find((c) => c.key === k)).filter(Boolean) as TraderColumn[];
  const infoOf = (col: TraderColumn) => (col.info || col.blurb) && (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground cursor-help"><Info size={13} /></span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] text-xs">{t((col.info || col.blurb) as string)}</TooltipContent>
    </Tooltip>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
          {t('top.columns')} <Badge variant="secondary" className="h-4 px-1 text-[10px]">{shown.length}</Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 max-h-[420px] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold">{t('top.showColumns')} <span className="font-normal text-muted-foreground">{t('top.dragToReorder')}</span></p>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => onChange(defaults)}>
            {t('top.reset')}
          </Button>
        </div>
        <div className="space-y-1.5">
          {rows.map((col, idx) => (
            <div
              key={col.key}
              draggable
              onDragStart={(e) => {
                setDragKey(col.key);
                setDraft(order);
                e.dataTransfer.effectAllowed = 'move';
                // Firefox starts no drag at all without payload.
                e.dataTransfer.setData('text/plain', col.key);
              }}
              onDragEnter={() => { if (dragKey && dragKey !== col.key) setDraft((d) => moveTo(dragKey, idx, d ?? order)); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); commit(); }}
              onDragEnd={commit}
              className={`flex items-center gap-2 text-sm py-1 px-1 -mx-1 rounded cursor-grab active:cursor-grabbing ${
                dragKey === col.key ? 'opacity-40 bg-muted' : 'hover:bg-muted/50'
              }`}
            >
              <span className="text-muted-foreground shrink-0" aria-hidden><DotsSixVertical size={14} /></span>
              <Checkbox checked onCheckedChange={() => toggle(col.key)} aria-label={t('mobile:leaderboard.hideColumn', { label: t(col.label) })} />
              <span className="flex-1 truncate text-muted-foreground">{t(col.label)}</span>
              {infoOf(col)}
              {/* The keyboard's way to the same order. */}
              <span className="flex shrink-0">
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={idx === 0}
                  aria-label={t('top.moveEarlier', { label: t(col.label) })}
                  onClick={() => onChange(moveTo(col.key, idx - 1, visible))}>
                  <CaretUp size={11} />
                </Button>
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={idx === rows.length - 1}
                  aria-label={t('top.moveLater', { label: t(col.label) })}
                  onClick={() => onChange(moveTo(col.key, idx + 1, visible))}>
                  <CaretDown size={11} />
                </Button>
              </span>
            </div>
          ))}
        </div>
        {TRADER_COLUMNS.some((c) => !visible.includes(c.key)) && (
          <>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-3 mb-1.5">{t('top.hidden')}</p>
            <div className="space-y-1.5">
              {TRADER_COLUMNS.filter((c) => !visible.includes(c.key)).map((col) => (
                <div key={col.key} className="flex items-center gap-2 text-sm py-0.5">
                  <Checkbox checked={false} onCheckedChange={() => toggle(col.key)} />
                  <span className="flex-1 truncate text-muted-foreground">{t(col.label)}</span>
                  {infoOf(col)}
                </div>
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
